/* Tech Sofa: size and product rails, built on Swiper (the agency standard for sliders).
   Column count and gap come from CSS (--ts-cols and --ts-gap on :root, set by the responsive tiers in base-new.css),
   so the counter and clamping stay right even when the section renders with no layout box (theme editor, hidden tabs). */
if (!customElements.get('ts-carousel')) {
  customElements.define('ts-carousel', class TsCarousel extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-viewport]');
      this.track = this.querySelector('.swiper-wrapper');
      if (!this.viewport || !this.track || this.ready) return;
      if (!window.Swiper) {
        window.addEventListener('load', () => this.connectedCallback(), { once: true });
        return;
      }
      this.ready = true;
      this.counter = this.querySelector('[data-counter]');
      this.prev = this.querySelector('[data-dir="-1"]');
      this.next = this.querySelector('[data-dir="1"]');
      this.tabs = Array.from(this.querySelectorAll('[data-tab]'));
      this.allSlides = Array.from(this.track.children);
      this.colsVar = this.dataset.colsVar || '--ts-cols';
      this.gapVar = this.dataset.gapVar || '--ts-gap';
      this.ofLabel = this.dataset.ofLabel || 'of';

      const css = this.readCss();
      this.swiper = new Swiper(this.viewport, {
        slidesPerView: css.cols,
        spaceBetween: css.gap,
        slidesPerGroup: 1,
        speed: 550,
        threshold: 4,
        resistanceRatio: 0.72,
        grabCursor: true,
        watchOverflow: true,
        navigation: { prevEl: this.prev, nextEl: this.next, disabledClass: 'is-disabled', lockClass: 'is-locked' },
        a11y: {
          enabled: true,
          slideRole: 'group',
          prevSlideMessage: (this.prev && this.prev.getAttribute('aria-label')) || 'Previous',
          nextSlideMessage: (this.next && this.next.getAttribute('aria-label')) || 'Next',
        },
        on: {
          beforeResize: () => this.applyCss(),
          slideChange: () => this.sync(),
          update: () => this.sync(),
          resize: () => this.sync(),
        },
      });

      this.tabs.forEach((t) => t.addEventListener('click', () => this.selectTab(t)));

      /* theme editor: bring the selected tile, card or tab into view */
      this.addEventListener('shopify:block:select', (e) => {
        const el = e.target;
        if (el.hasAttribute('data-tab')) { this.selectTab(el); return; }
        const slide = el.closest('.swiper-slide');
        if (slide && !slide.isConnected && this.tabs[0]) this.selectTab(this.tabs[0]);
        const i = this.swiper.slides.indexOf(slide);
        if (i > -1) this.swiper.slideTo(i);
      });

      /* TrustBoxes load themselves on page load; a section re-rendered in the theme editor needs a nudge */
      if (window.Trustpilot && window.Trustpilot.loadFromElement) {
        this.querySelectorAll('.trustpilot-widget').forEach((el) => { if (!el.querySelector('iframe')) window.Trustpilot.loadFromElement(el, true); });
      }

      this.onReorder = () => { this.swiper.update(); };
      document.addEventListener('shopify:section:reorder', this.onReorder);
      this.sync();
    }

    disconnectedCallback() {
      document.removeEventListener('shopify:section:reorder', this.onReorder);
      if (this.swiper) { this.swiper.destroy(true, true); this.swiper = null; }
      this.ready = false;
    }

    readCss() {
      const root = getComputedStyle(document.documentElement);
      return {
        cols: parseInt(root.getPropertyValue(this.colsVar), 10) || 4,
        gap: parseFloat(root.getPropertyValue(this.gapVar)) || 0,
      };
    }

    /* the CSS tiers changed: hand the new column count and gap to Swiper before it recalculates */
    applyCss() {
      const css = this.readCss();
      this.swiper.params.slidesPerView = css.cols;
      this.swiper.params.spaceBetween = css.gap;
    }

    selectTab(tab) {
      this.tabs.forEach((t) => {
        const on = t === tab;
        t.classList.toggle('is-on', on);
        t.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      const want = tab.dataset.tab;
      const show = this.allSlides.filter((s) => want === '*' || s.dataset.series === want);
      this.track.replaceChildren(...show);
      this.swiper.update();
      this.swiper.slideTo(0, 0);
      this.sync();
    }

    sync() {
      if (!this.swiper || this.swiper.destroyed) return;
      const cols = this.readCss().cols;
      const total = this.swiper.slides.length;
      const i = Math.min(this.swiper.activeIndex, Math.max(0, total - cols));
      if (this.counter) {
        this.counter.textContent = total
          ? (i + 1) + '–' + Math.min(i + cols, total) + ' ' + this.ofLabel + ' ' + total
          : '';
      }
      if (this.prev) this.prev.disabled = this.swiper.isBeginning;
      if (this.next) this.next.disabled = this.swiper.isEnd;
    }
  });
}
